// §34.3/§34.4 - two-step availability-first scheduling flow, replacing the
// legacy bare datetime "Schedule" card (see D-34-22): Step 1 picks a day from
// the availability calendar, Step 2 picks a bay + start time on that day's
// grid. The installer enters the job duration in hours (defaulting to the
// job's estimate); a job longer than one day's remaining hours spans into the
// following open days, so this fetches those days' detail and hands the whole
// run to the grid.
import { useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { DayDetail, JobDetail } from '@wrapforce/shared';
import { addDaysToIsoDate, planDaySpans } from '@wrapforce/shared';
import { apiErrorMessage } from '@/lib/api-errors';
import { PLAN_DEFINITIONS } from '@wrapforce/shared/constants';
import { useActiveMembership } from '@/features/session/useActiveMembership';
import { AvailabilityCalendar } from '@/features/scheduling/AvailabilityCalendar';
import { DayGrid, type ConfirmArgs } from '@/features/scheduling/DayGrid';
import { timeRowsForWindow } from '@/features/scheduling/dayGridUtils';
import { useDayDetail, useDayDetails, useScheduleJob } from '@/features/scheduling/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FieldError, Input, Label } from '@/components/ui/input';
import { useLocaleFormatters } from '@/lib/format';

const MAX_SPAN_DAYS = 14;

/** Minutes as an hours string with no trailing zeros: 450 -> "7.5", 120 -> "2".
 * A 0 estimate (e.g. a quote whose lines carried no labor minutes) is as
 * useless as no estimate, so it maps to an empty field rather than "0". */
function minutesToHoursInput(minutes: number | null): string {
  return minutes == null || minutes <= 0 ? '' : String(minutes / 60);
}

export function SchedulingFlow({ workspaceId, jobId, job }: { workspaceId: string | null; jobId: string; job: JobDetail }) {
  const { t } = useTranslation('jobs');
  const { formatDateTime } = useLocaleFormatters();
  const { membership } = useActiveMembership();
  const gateOn = membership ? PLAN_DEFINITIONS[membership.plan].gates.multiLocation : false;
  const schedulingLocationId = gateOn ? job.locationId : undefined;
  const currentDate = job.scheduledStart ? format(new Date(job.scheduledStart), 'yyyy-MM-dd') : null;

  const [flowOpen, setFlowOpen] = useState(!job.scheduledStart);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [hoursInput, setHoursInput] = useState(() => minutesToHoursInput(job.estimatedMinutes));
  const [durationDirty, setDurationDirty] = useState(false);
  const [continuationCount, setContinuationCount] = useState(0);

  const hoursNum = Number(hoursInput);
  const durationValid = hoursInput.trim() !== '' && Number.isFinite(hoursNum) && hoursNum > 0;
  const durationMinutes = durationValid ? Math.round(hoursNum * 60) : 0;

  const displayDate = flowOpen ? selectedDate : currentDate;
  const { data: dayDetail } = useDayDetail(workspaceId, displayDate, jobId, schedulingLocationId);
  const scheduleJob = useScheduleJob(workspaceId, jobId);

  // If the estimate changes upstream while the form is open and the installer
  // hasn't touched the field, follow the new default; once dirty, never clobber.
  const prevEstimate = useRef(job.estimatedMinutes);
  useEffect(() => {
    if (prevEstimate.current !== job.estimatedMinutes) {
      prevEstimate.current = job.estimatedMinutes;
      if (!durationDirty) setHoursInput(minutesToHoursInput(job.estimatedMinutes));
    }
  }, [job.estimatedMinutes, durationDirty]);

  // A fresh day or duration resets how many continuation days we've paged in.
  useEffect(() => setContinuationCount(0), [selectedDate, durationMinutes]);

  const continuationDates = useMemo(
    () => (selectedDate ? Array.from({ length: continuationCount }, (_, i) => addDaysToIsoDate(selectedDate, i + 1)) : []),
    [selectedDate, continuationCount],
  );
  const continuationQueries = useDayDetails(workspaceId, continuationDates, jobId, schedulingLocationId);

  const continuationDetails: DayDetail[] = [];
  for (const q of continuationQueries) {
    if (q.data) continuationDetails.push(q.data);
    else break;
  }
  const allContinuationLoaded = continuationDetails.length === continuationDates.length;
  // A failed day fetch (or hitting the span cap below) must terminate the
  // growth loop, otherwise the grid stays on "resolving availability" forever.
  const continuationStalled = continuationQueries.some((q) => q.isError);
  const days = dayDetail ? [dayDetail, ...continuationDetails] : [];
  const filteredDays = useMemo(() => {
    if (!gateOn) return days;
    return days.map((d) => ({ ...d, bays: d.bays.filter((b) => b.locationId === job.locationId) }));
  }, [days, gateOn, job.locationId]);

  // Whether even the last possible start on day 1 would need more days than
  // we've fetched so far. Computed synchronously from `days` (not just inside
  // the growth effect below) so the grid can stay hidden until this is false,
  // instead of briefly rendering open-slot data for a too-short `days` window
  // (which makes every bay - not just a genuinely conflicted one - look
  // unavailable on the slots that would spill past the fetched days).
  const needsMoreDays = useMemo(() => {
    if (!dayDetail?.window || !durationValid) return false;
    const rows = timeRowsForWindow(dayDetail.window);
    const lastRow = rows[rows.length - 1];
    if (!lastRow) return false;
    const plan = planDaySpans({ startsAt: lastRow, durationMinutes, days: days.map((d) => ({ date: d.date, window: d.window })) });
    return !plan.ok && plan.reason === 'insufficient_days';
  }, [dayDetail, durationMinutes, durationValid, days]);

  // Grow the fetched run one day at a time until even the last possible start
  // on day 1 has enough open days to fit the job (or we hit the 14-day cap).
  useEffect(() => {
    if (!needsMoreDays || !allContinuationLoaded || continuationStalled || continuationCount >= MAX_SPAN_DAYS - 1) return;
    setContinuationCount((c) => c + 1);
  }, [needsMoreDays, allContinuationLoaded, continuationStalled, continuationCount]);

  // Ready once growth has settled: either every start fits in the fetched
  // window, or we can't fetch further (cap reached / a fetch failed) - in the
  // latter case the grid renders with what we have and late starts that would
  // spill past it simply show as unavailable.
  const growthDone = continuationStalled || continuationCount >= MAX_SPAN_DAYS - 1;
  const gridReady = (allContinuationLoaded || continuationStalled) && (!needsMoreDays || growthDone);

  const currentSegment = dayDetail?.segments.find((s) => s.jobId === jobId && s.kind === 'work');
  const currentBay = currentSegment ? dayDetail?.bays.find((b) => b.id === currentSegment.bayId) : undefined;

  async function handleConfirm(args: ConfirmArgs) {
    try {
      const input = 'dayBays' in args
        ? { startsAt: args.startsAt, dayBays: args.dayBays, durationMinutes }
        : { startsAt: args.startsAt, bayId: args.bayId, durationMinutes };
      await scheduleJob.mutateAsync(input);
      toast.success(t('toast.scheduled'));
      setFlowOpen(false);
      setSelectedDate(null);
    } catch (err) {
      toast.error(t('scheduling.genericError'), { description: apiErrorMessage(err) });
    }
  }

  if (!flowOpen && job.scheduledStart) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('card.schedule')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-3">
          <div className="text-sm text-ink">
            {currentBay ? <div>{t('scheduling.currentBay', { bay: currentBay.name })}</div> : null}
            <div className="text-xs text-muted">
              {formatDateTime(new Date(job.scheduledStart))}
              {job.scheduledEnd ? ` - ${formatDateTime(new Date(job.scheduledEnd))}` : ''}
            </div>
          </div>
          <Button size="sm" variant="secondary" className="ml-auto" onClick={() => setFlowOpen(true)}>
            {t('scheduling.reschedule')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('card.schedule')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <AvailabilityCalendar
          workspaceId={workspaceId}
          preferredDate={job.preferredDate}
          selectedDate={selectedDate}
          onSelectDay={setSelectedDate}
          locationId={schedulingLocationId}
        />

        {selectedDate && dayDetail ? (
          <div className="flex flex-col gap-3 border-t border-border pt-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="duration">{t('scheduling.duration')}</Label>
              <Input
                id="duration"
                inputMode="decimal"
                value={hoursInput}
                onChange={(e) => {
                  setHoursInput(e.target.value);
                  setDurationDirty(true);
                }}
                placeholder={t('scheduling.durationHoursPlaceholder')}
                className="w-32"
              />
              {!durationValid && hoursInput.trim() !== '' ? <FieldError>{t('scheduling.durationInvalid')}</FieldError> : null}
            </div>

            {durationValid ? (
              gridReady ? (
                <DayGrid
                  days={filteredDays}
                  durationMinutes={durationMinutes}
                  cureHoldMinutes={job.cureHoldMinutes ?? 0}
                  preferredTime={job.preferredTime ?? null}
                  onConfirm={handleConfirm}
                  confirmPending={scheduleJob.isPending}
                />
              ) : (
                <p className="text-xs text-muted">{t('scheduling.resolvingAvailability')}</p>
              )
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
